import React, {useEffect, useMemo, useRef, useState} from "react";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {hopnLanguageArray, formatPrice} from "@/utils/functions.js";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {paymentService} from "@/services/paymentService";
import {useWalletBalance} from "@/hooks/useWallet";
import {filterSelectedCarDetails} from "@/utils/filesConstants.js";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {isMissingRequiredFlightNumber, FLIGHT_NUMBER_REQUIRED_MESSAGE} from "@/utils/airports";
import {
    bigClose,
    blackSquareLogo,
    infoIcon,
    squareLogo,
    mastercardLogo,
    visaLogo,
    amexLogo,
    interacLogo,
    appleIcon,
    googleIcon,
    carPlane,
    qrCode,
    appStoreButton,
    playStoreButton, logoLatest,
    lock, smartTimeQuestionIcon, arrowDown,
} from "@img/index.js";
import {Swiper, SwiperSlide} from "swiper/react";
import "swiper/css";
import {ModalFooter} from "../Components/BookingModalShell.jsx";
import {PayAfterDropoffContent} from "./payAfterDropoff.jsx";
import {HopnReviewsWidget} from "../Components/hopnReviewsWidget.jsx";
import {useAppConfig} from "@/hooks/useAppConfig";
import {useUser} from "@/hooks/useUser";
import {resolveSquare} from "@/utils/square";


/**
 * "No payment today" — the reassurance block above the card field.
 *
 * Two rows of flat green-tinted text became a black headline bar over a grey panel, with
 * the "why" folded away behind a disclosure (2026-08-21, from the user's mockups). The
 * point is height: this step also carries the Square field, the saved cards, the wallet
 * and the Reserve button, and the old block spent a fifth of the modal on copy nobody
 * reads twice. Folded, it now costs three lines.
 *
 * Deliberately NOT `.hopn-no-payment-box`: that class is shared with the Pay-after-drop-off
 * popup (modalPages/payAfterDropoff.jsx), which is a page ABOUT this reassurance and wants
 * every word visible. Restyling it in place would have silently rebuilt that popup too.
 *
 * MIRRORS the plugin's copy in `modalPages/paymentCards.jsx` — same markup, same classes,
 * same SCSS. Change one, change the other.
 *
 * EXPORTED because the edit/confirm pages' add-card popup (PaymentMethodsPopup) shows the
 * same reassurance and must not drift from this one. It takes no props: every word is
 * fixed copy, and a second instance differing only in wording would be the bug.
 */
export function NoPaymentToday() {
    const [open, setOpen] = useState(false);

    return (
        <div className="hopn-npt">
            <div className="hopn-npt-bar">
                <img src={lock} alt=""/>
                <strong>No payment today</strong>
            </div>

            <div className="hopn-npt-panel">
                <p className="hopn-npt-lede">
                    <b>Square</b> securely holds your card and charges only after your ride is completed.
                </p>

                <div className={`hopn-npt-why ${open ? "is-open" : ""}`}>
                    {/* A real <button>: this is the one interactive thing in the block, and
                        it has to answer the keyboard and announce its own state. */}
                    <button
                        type="button"
                        className="hopn-npt-why-head"
                        aria-expanded={open}
                        onClick={() => setOpen((v) => !v)}
                    >
                        <img className="hopn-npt-why-icon" src={smartTimeQuestionIcon} alt=""/>
                        <span>Why do we need your card?</span>
                        <img className="hopn-npt-why-chev" src={arrowDown} alt=""/>
                    </button>

                    {open && (
                        <ul className="hopn-npt-why-list">
                            <li>Reserve a dedicated driver.</li>
                            <li>Prevent fraudulent or duplicate bookings.</li>
                            <li>Cancel on time, <b>no charge will be made.</b></li>
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}



// Wallet (Apple Pay / Google Pay) paymentRequest country. NOT served by /config/app —
// the config has no country field — so this stays local. The CURRENCY used to sit beside
// it as a hardcoded "CAD" with a TODO to source it from the config; the config does carry
// it (`square.{sandbox,production}.currency_code`), so resolveSquare() now returns it and
// the constant is gone. Square derives the real currency from the merchant account, so
// taking it from the same block that supplies the ids keeps the two in step.
const WALLET_COUNTRY = "CA";

// Apple Pay / Google Pay are BUILT but HIDDEN. They can't complete a payment yet: Apple Pay
// needs the domain registered with Square, and the backend can't store a wallet token.
//
// They used to render disabled, so the payment options kept their intended shape — that was
// worse than absent. A greyed-out Apple Pay button reads as "your device can't do this"
// rather than "we haven't finished building it", and it invites a tap that can never work.
// Now the whole block (the OR divider included) is gated on this flag, as is wallet init —
// there is no point paying for the Square handshake for buttons nobody can see.
//
// Flip to `true` to bring them back; nothing else was changed to hide them.
// Mirrors the same constant in the plugin's `modalPages/paymentCards.jsx`.
const WALLET_PAY_ENABLED = false;

// Shared Secure Payment content — everything EXCEPT the step's own ModalHeader
// shell (each caller supplies its own header). Rendered by the PaymentCards step,
// the PersonalCard mobile step, and the "Personal Card" tab's desktop .upper-modal popup.
export function PaymentCardsContent({setStep}) {
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [loadingCards, setLoadingCards] = useState(false);
    const [cards, setCards] = useState([]);
    const [cardError, setCardError] = useState("");
    const [showAppPopup, setShowAppPopup] = useState(false);
    const [showPaymentTooltip, setShowPaymentTooltip] = useState(false);
    const [innerModalPopup, setInnerModalPopup] = useState(false);

    const [isSquareLoading, setIsSquareLoading] = useState(true);
    const [submitButton, setSubmitButton] = useState(false);
    const [squareCard, setSquareCard] = useState(null);
    const [useWallet, setUseWallet] = useState(false);
    const mountedRef = useRef(true);
    const squareCardRef = useRef(null);

    // Apple Pay / Google Pay (Square Web Payments SDK wallet methods) — created off the
    // same `payments` object once the card SDK is ready. Only usable once (a) the domain
    // is registered with Square for Apple Pay, (b) the device/browser supports the wallet,
    // and (c) the backend can store the wallet token as a card-on-file.
    const applePayRef = useRef(null);
    const googlePayRef = useRef(null);
    const [applePayReady, setApplePayReady] = useState(false);
    const [googlePayReady, setGooglePayReady] = useState(false);
    const [walletBusy, setWalletBusy] = useState(false);

    // Per-user Square credentials: /config/app has the sandbox+production id
    // sets; GET /account's is_test_account picks which (refetched on auth change).
    const {data: appConfig} = useAppConfig();
    const {data: account} = useUser();
    const squareCreds = resolveSquare(appConfig?.square, account?.is_test_account);

    const {data: balance} = useWalletBalance();
    const tripTotalCents = useMemo(() => {
        try {
            return Number(filterSelectedCarDetails()?.total_price) || 0;
        } catch {
            return 0;
        }
    }, []);
    // Credits are gated by BOTH flags the API calls authoritative: /config/app's credit.enabled
    // and the balance payload's own. Config is the earlier signal — fetched once at boot, so the
    // section never renders when the wallet is switched off, while the balance query is cached for
    // 2 minutes. `!== false` on purpose: a missing key must not hide a working wallet, the balance
    // flag still gates it.
    const creditEnabled = appConfig?.credit?.enabled !== false;
    const walletEnabled = creditEnabled && !!balance?.enabled && Number(balance?.available_cents) > 0;
    const walletCents = Number(balance?.available_cents) || 0;
    const walletCoversAll = walletEnabled && walletCents >= tripTotalCents && tripTotalCents > 0;
    const walletPartial = walletEnabled && useWallet && walletCents < tripTotalCents;
    const walletAmountToUse = useWallet ? Math.min(walletCents, tripTotalCents) : 0;
    // Amount actually drawn from credits for THIS trip = min(balance, trip total).
    // The full balance is shown next to the "Credit" heading; this is what the
    // button applies now.
    const walletApplyCents = tripTotalCents > 0 ? Math.min(walletCents, tripTotalCents) : walletCents;

    useEffect(() => {
        storage.set(STORAGE_KEYS.PAYMENT_DETAILS_POLICIES, false);
    }, []);

    useEffect(() => {
        const cachedCards = storage.get(STORAGE_KEYS.USER_SAVED_CARDS);
        if (cachedCards) setCards(cachedCards);
        else fetchPaymentMethods();
    }, []);

    // Pre-select once the list lands: the card already stored (USER_CARD_ID) if it is
    // still in the list, else the first one — so "Available Cards" opens with the same
    // card selectedCarDetailsLatest names under "Credit/Debit Card", instead of nothing.
    // Guarded by a ref, NOT by `selectedCardId === null`: tapping the selected card
    // DESELECTS it (see the checkbox below), and a null-guard would instantly re-select.
    const didAutoSelect = useRef(false);
    useEffect(() => {
        if (didAutoSelect.current || !cards.length) return;
        didAutoSelect.current = true;
        const stored = storage.get(STORAGE_KEYS.USER_CARD_ID);
        const match = cards.find((c) => String(c.id) === String(stored));
        setSelectedCardId(match ? match.id : cards[0].id);
    }, [cards]);

    const retryCountRef = useRef(0);
    const MAX_RETRIES = 3;

    useEffect(() => {
        mountedRef.current = true;
        loadSquare();
        return () => {
            mountedRef.current = false;
            if (squareCardRef.current) {
                try {
                    squareCardRef.current.destroy();
                } catch (_) {
                }
                squareCardRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [squareCreds.appId, squareCreds.status]);

    const fetchPaymentMethods = async () => {
        setLoadingCards(true);
        setCardError("");
        try {
            const result = await paymentService.getPaymentMethods();
            setLoadingCards(false);
            if (result.success && result.data.length > 0) {
                setCards(result.data);
                storage.set(STORAGE_KEYS.USER_SAVED_CARDS, result.data);
            } else if (result.success && result.data.length === 0) {
                setCardError("No saved cards found");
                setCards([]);
            } else {
                setCardError(result.error || "Failed to load payment methods");
                setCards([]);
            }
        } catch (error) {
            setLoadingCards(false);
            setCardError("Failed to load payment methods");
            setCards([]);
        }
    };

    const nukeSquare = ({keepScript = false} = {}) => {
        if (squareCardRef.current) {
            try {
                squareCardRef.current.destroy();
            } catch (_) {
            }
            squareCardRef.current = null;
        }
        if (!keepScript) {
            const scripts = document.querySelectorAll('script[src*="squarecdn.com"]');
            scripts.forEach(s => s.remove());
            delete window.Square;
        }
        const container = document.getElementById('card');
        if (container) container.innerHTML = '';
    };

    const ensureSquareScript = (sdkSrc) => {
        if (window.Square) return Promise.resolve();
        const existing = document.querySelector('script[src*="squarecdn.com"]');
        if (existing) {
            return new Promise((resolve, reject) => {
                existing.addEventListener('load', () => resolve(), {once: true});
                existing.addEventListener('error', reject, {once: true});
            });
        }
        const script = document.createElement('script');
        script.src = sdkSrc;
        script.async = true;
        document.body.appendChild(script);
        return new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
        });
    };

    const loadSquare = async (isRetry = false) => {
        setIsSquareLoading(true);
        if (!isRetry) retryCountRef.current = 0;
        nukeSquare({keepScript: true});

        // Creds depend on /config/app (id sets) + is_test_account (GET /account).
        // While is_test_account isn't known yet keep loading (never guess an
        // environment); show "unavailable" only once known but the id set is missing.
        const {appId, locationId, sdkSrc, status} = squareCreds;
        if (status === "pending") {
            setIsSquareLoading(true);
            return;
        }
        if (status !== "ready") {
            setIsSquareLoading(false);
            setErrorMessage("Card payment is temporarily unavailable. Please try again later.");
            return;
        }

        try {
            await ensureSquareScript(sdkSrc);

            if (!mountedRef.current) return;
            if (!window.Square) throw new Error('Square not available after script load');

            const payments = window.Square.payments(appId, locationId);
            // Hardcode a light theme so the card NEVER follows the browser's
            // prefers-color-scheme — Square's SDK renders its dark theme otherwise.
            const card = await payments.card({
                style: {
                    input: {color: "#2b2b2b", backgroundColor: "#ffffff"},
                    "input::placeholder": {color: "#8d8e8f"},
                    ".input-container": {borderColor: "#cfcdcd", borderRadius: "8px"},
                    ".input-container.is-focus": {borderColor: "#2b2b2b"},
                    ".input-container.is-error": {borderColor: "#d0021b"},
                    ".message-text": {color: "#8d8e8f"},
                    ".message-icon": {color: "#8d8e8f"},
                    ".message-text.is-error": {color: "#d0021b"},
                    ".message-icon.is-error": {color: "#d0021b"},
                },
            });

            if (!mountedRef.current) {
                try {
                    card.destroy();
                } catch (_) {
                }
                return;
            }

            await card.attach("#card");

            if (!mountedRef.current) {
                try {
                    card.destroy();
                } catch (_) {
                }
                return;
            }

            squareCardRef.current = card;
            retryCountRef.current = 0;
            setIsSquareLoading(false);
            setSubmitButton(true);
            setSquareCard(card);
            setShowPaymentTooltip(true);

            // Spin up Apple Pay / Google Pay off the same payments object (non-blocking).
            initWallets(payments);
        } catch (error) {
            console.error("Square init failed:", error);
            if (!mountedRef.current) return;
            nukeSquare({keepScript: true});
            if (retryCountRef.current < MAX_RETRIES) {
                retryCountRef.current += 1;
                const delay = retryCountRef.current * 1500;
                await new Promise(r => setTimeout(r, delay));
                if (mountedRef.current) return loadSquare(true);
            }
            window.location.reload();
        }
    };

    // Shared: persist a Square token (card field OR wallet) as a card-on-file, then
    // advance. `onFail(msg)` lets each caller restore its own button state.
    const saveTokenAndProceed = async (result, onFail) => {
        storage.set(STORAGE_KEYS.CARD_TOKEN, result.token);

        const firstName = storage.get(STORAGE_KEYS.FIRST_NAME, "");
        const lastName = storage.get(STORAGE_KEYS.LAST_NAME, "");
        const full_name = `${firstName} ${lastName}`.trim() || "Cardholder";

        const saveCard = await paymentService.storeCard({
            squareToken: result.token,
            displayName: full_name,
            isDefault: true,
            metadata: result.details
        });

        const savedCard = saveCard.data?.payment_method || saveCard.data;

        if (saveCard.success && savedCard?.id) {
            storage.set(STORAGE_KEYS.USER_CARD_ID, savedCard.id);
            setStep("processingBooking");
        } else {
            onFail(saveCard.error || "Card was authorized but could not be saved. Please try again or use a different card.");
        }
    };

    const handleTokenizeAndSubmit = async () => {
        if (!squareCard) return;
        setSubmitButton(false);
        setErrorMessage("");

        const result = await squareCard.tokenize();

        if (result.status === "OK") {
            await saveTokenAndProceed(result, (msg) => {
                setErrorMessage(msg);
                setSubmitButton(true);
            });
        } else {
            setSubmitButton(true);
        }
    };

    // Create the Apple Pay / Google Pay instances off the ready `payments` object.
    // Guarded per wallet: an unsupported wallet (or an unregistered Apple Pay domain)
    // just leaves that button in its "unavailable" state. Needs a non-zero trip total.
    const initWallets = async (payments) => {
        // Switched off with the buttons — no point paying for the wallet handshake (and
        // its console noise) when nothing clickable renders.
        if (!WALLET_PAY_ENABLED) return;
        if (!(tripTotalCents > 0)) return;
        let paymentRequest;
        try {
            paymentRequest = payments.paymentRequest({
                countryCode: WALLET_COUNTRY,
                currencyCode: squareCreds.currencyCode,
                total: {amount: (tripTotalCents / 100).toFixed(2), label: "Total"},
            });
        } catch (_) { return; }

        try {
            const applePay = await payments.applePay(paymentRequest);
            applePayRef.current = applePay;
            if (mountedRef.current) setApplePayReady(true);
        } catch (_) { /* Apple Pay unavailable (unsupported browser / unregistered domain) */ }

        try {
            const googlePay = await payments.googlePay(paymentRequest);
            googlePayRef.current = googlePay;
            if (mountedRef.current) setGooglePayReady(true);
        } catch (_) { /* Google Pay unavailable */ }
    };

    // Tap handler for the Apple Pay / Google Pay buttons — tokenize via the wallet and
    // save the token exactly like a typed card.
    const handleWalletPay = async (which) => {
        const instance = which === "apple" ? applePayRef.current : googlePayRef.current;
        const label = which === "apple" ? "Apple Pay" : "Google Pay";
        setErrorMessage("");
        if (!instance) {
            setErrorMessage(`${label} isn't available on this device or browser.`);
            return;
        }
        setWalletBusy(true);
        try {
            const result = await instance.tokenize();
            if (result.status === "OK") {
                await saveTokenAndProceed(result, (msg) => {
                    setErrorMessage(msg);
                    setWalletBusy(false);
                });
            } else {
                setWalletBusy(false);
            }
        } catch (_) {
            setErrorMessage(`${label} payment could not be completed.`);
            setWalletBusy(false);
        }
    };

    const handleSubmit = () => {
        setErrorMessage("");

        // Last gate before the booking request. orderDetails already refuses to send a
        // from-airport booking with no flight number, so this only catches a state that
        // slipped past it — better an inline sentence here than a 422 on the processing
        // screen, which reads as a payment failure.
        if (isMissingRequiredFlightNumber()) {
            setErrorMessage(FLIGHT_NUMBER_REQUIRED_MESSAGE);
            return;
        }

        storage.set(STORAGE_KEYS.USE_WALLET_AMOUNT, walletAmountToUse > 0 ? walletAmountToUse : null);

        if (walletCoversAll && useWallet) {
            storage.set(STORAGE_KEYS.USER_CARD_ID, null);
            setStep("processingBooking");
            return;
        }

        if (selectedCardId) {
            storage.set(STORAGE_KEYS.USER_CARD_ID, selectedCardId);
            setStep("processingBooking");
            return;
        }

        if (submitButton && squareCard) {
            handleTokenizeAndSubmit();
            return;
        }

        setStep("processingBooking");
    };

    const getButtonText = () => {
        if (walletCoversAll && useWallet && !selectedCardId) return "Pay with credits — Continue";
        if (selectedCardId) return "Pay after drop-off — Continue";
        if (!submitButton) return <Loader color="#fff"/>;
        return "Pay after drop-off — Continue";
    };

    return (
        <>
            {/* Pay after drop-off inner popup (desktop) — opened from the Square tooltip info icon */}
            <div className="upper-modal desktop" style={{display: innerModalPopup ? "flex" : "none"}} hopn-data-category="modal_add_card">
                <div className="upper-modal-inner">
                    <ModalHeader
                        inline={true}
                        title=""
                        extraClasses=""
                        totalSteps={0}
                        cancelButton={true}
                        cancelButtonType="text"
                        backStep={null}
                        showBackButton={false}
                        setStep={setStep}
                        closeModal={() => { setInnerModalPopup(false); setShowPaymentTooltip(true); }}
                    />
                    <div className="upper-modal-content padding-bottom-30">
                        <div className="modalPages-spacing">
                            <PayAfterDropoffContent/>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modalPages-spacing">
                {/* Header — Secure Payment + Powered by Square */}
                <div className="hopn-secure-pay-head">
                    <h3 className="sub-title less-space payment-add-card-title">Secure Payment</h3>
                    <p className="hopn-powered-by">
                        Powered by
                        <img src={blackSquareLogo} alt="Square"/>
                        <strong>Square</strong>
                    </p>
                </div>

                {/* No payment today — see NoPaymentToday. */}
                <NoPaymentToday/>

                {/* Credit or Debit Card */}
                <h6 className="hopn-cc-label">Credit or Debit Card</h6>
                <div className="payment-card-add-card payment-card-step">
                    <div id="wrapper">
                        <center>{isSquareLoading && <Loader/>}</center>
                        <div id="card"></div>
                    </div>
                </div>

                {errorMessage && (
                    <div className="hopn-payment-error hopn-payment-error--card">
                        {errorMessage}
                    </div>
                )}

                {/* OR — Apple Pay / Google Pay. HIDDEN ENTIRELY while `WALLET_PAY_ENABLED` is
                    false; see the note there. When live it shows only once the Square SDK is up
                    (no SDK, no wallets at all), and both buttons render regardless of which
                    wallet the device supports — handleWalletPay reports "<wallet> isn't
                    available on this device or browser" for the one that isn't usable here. */}
                {WALLET_PAY_ENABLED && !isSquareLoading && squareCard && (
                    <>
                        <div className="hopn-wallet-or"><span>OR</span></div>
                        <div className="hopn-wallet-buttons">
                            <button type="button" className="hopn-wallet-btn hopn-wallet-btn--apple" onClick={() => handleWalletPay('apple')} disabled={!WALLET_PAY_ENABLED || walletBusy}>
                                <img src={appleIcon} alt=""/>
                                <span>Pay</span>
                            </button>
                            <button type="button" className="hopn-wallet-btn hopn-wallet-btn--google" onClick={() => handleWalletPay('google')} disabled={!WALLET_PAY_ENABLED || walletBusy}>
                                <img src={googleIcon} alt=""/>
                                <span>Pay</span>
                            </button>
                        </div>
                    </>
                )}

                <div className="payment-scenarios">
                    <div className="hopn-payment-types-container">
                        <div className="hopn-payment-types-available">
                            <div className="hopn-available-cards-wrapper is-visible">
                                <div className="hopn-payment-types-available-header">
                            <span className="hopn-payment-types-available-title" hopn-data-category="payment_type"
                                  hopn-data-index="available_cards">
                                {hopnLanguageArray("payment_type", "available_cards", "Available Cards")}
                            </span>
                                </div>

                                {loadingCards && (
                                    <div className="hopn-cards-loading">
                                        <Loader/>
                                    </div>
                                )}

                                {cardError && (
                                    <div className="hopn-cards-error">
                                        {cardError}
                                    </div>
                                )}

                                {/* A SLIDER, not a wrapping list — see the plugin's copy for
                                    the reasoning: a passenger with six saved cards got three
                                    rows of pills and pushed Reserve off the screen. One row
                                    that scrolls costs the same height at any card count. */}
                                {!loadingCards && cards.length > 0 && (
                                    <Swiper className="hopn-saved-cards-slider" slidesPerView="auto" spaceBetween={10}>
                                        {cards.map((card) => {
                                            let metadata = {};
                                            try {
                                                metadata = JSON.parse(card.metadata);
                                            } catch {
                                            }

                                            const brand = metadata?.card?.brand || "Card";
                                            const last4 = metadata?.card?.last4 || "****";

                                            return (
                                                <SwiperSlide key={card.id} className="hopn-saved-card-slide">
                                                <label
                                                    className={`hopn-saved-card-btn ${selectedCardId === card.id ? "is-selected" : ""}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCardId === card.id}
                                                        onChange={() => {
                                                            const next = selectedCardId === card.id ? null : card.id;
                                                            setSelectedCardId(next);
                                                            // Persist IMMEDIATELY, not just on submit: the
                                                            // "Credit/Debit Card" row on selectedCarDetailsLatest
                                                            // reads USER_CARD_ID when this panel closes, so the
                                                            // two must agree the moment the choice is made.
                                                            if (next) storage.set(STORAGE_KEYS.USER_CARD_ID, next);
                                                            else storage.remove(STORAGE_KEYS.USER_CARD_ID);
                                                            setErrorMessage("");
                                                        }}
                                                        hidden
                                                    />
                                                    <span className="card-title">{brand} ****{last4}</span>
                                                </label>
                                                </SwiperSlide>
                                            );
                                        })}
                                    </Swiper>
                                )}
                            </div>
                        </div>
                    </div>
                    {walletEnabled && (
                        <div className="hopn-payment-types-container min-w-50">
                            <div className="hopn-payment-types-available">
                                <div className="hopn-available-cards-wrapper is-visible">
                                    <div className="hopn-payment-types-available-header">
                                        <div className="hopn-credit-section-header">
                                            <img src={logoLatest} alt="hopn" className="hopn-credit-logo"/>
                                            <span className="hopn-credit-title">Credit</span>
                                            <span className="hopn-credit-balance">{formatPrice(walletCents, {decimals: 2})}</span>
                                        </div>
                                    </div>
                                    <div className="hopn-saved-cards">
                                        <label className={`hopn-saved-card-btn min-w-60 ${useWallet ? "is-selected" : ""}`}>
                                            <input
                                                type="checkbox"
                                                checked={useWallet}
                                                onChange={() => {
                                                    setUseWallet(prev => !prev);
                                                    setErrorMessage("");
                                                }}
                                                hidden
                                            />
                                            <span className="card-title">
                                        Use {formatPrice(walletApplyCents, {decimals: 2})} from credits
                                    </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                {walletPartial && (
                    <div className="hopn-quick-booking-error mb-8">
                        Your wallet amount is lesser than the trip total. Please select an available card or attach a
                        new card so we can charge the
                        remaining {formatPrice(tripTotalCents - walletCents, {decimals: 2})}.
                    </div>
                )}

                {/* Inline (non-sticky) footer — scrolls with the content */}
                <div className="hopn-pay-footer">
                    <button
                        className="hopn-reserve-ride-btn"
                        onClick={handleSubmit}
                        disabled={!(useWallet && walletCoversAll) && !selectedCardId && !submitButton}
                    >
                        {(!submitButton && !selectedCardId && !(useWallet && walletCoversAll)) ? (
                            <Loader color="#fff"/>
                        ) : (
                            <span className="hopn-reserve-ride-inner">
                                <span className="hopn-reserve-ride-title">
                                    <img src={lock} alt=""/>
                                    Reserve Ride
                                </span>
                                <small>You'll be charged after your drop-off.</small>
                            </span>
                        )}
                    </button>

                    {/* Trust strip — 18+ Years + live Google rating (Elfsight summary) */}
                    <div className="hopn-trust-strip">
                        <div className="hopn-trust-cell">
                            <strong>18+ Years</strong>
                            <span>In Business</span>
                        </div>
                        <div className="hopn-trust-cell">
                            <HopnReviewsWidget layout="summary"/>
                            <span>10k+ Safe Rides</span>
                        </div>
                    </div>
                </div>
            </div>

            {showAppPopup && (
                <div className="upper-modal" style={{display: "flex"}}>
                    <div className="upper-modal-inner">
                        <div className="download-app-details">
                            <div className="hopn-modal-header">
                                <div className="hopn-modal-header-top">
                                    <div className="inner" style={{display: "flex"}}>
                                        <img decoding="async" src={carPlane} alt="Car plane"/>
                                    </div>
                                    <div className="inner"></div>
                                </div>
                            </div>

                            <span className="title" hopn-data-category="payment_type"
                                  hopn-data-index="download_our_app_to_use_this_payment_method">
                                {hopnLanguageArray("payment_type", "download_our_app_to_use_this_payment_method", "Download our app to use this payment method.")}
                            </span>

                            <div className="hopn-download-app-section">
                                <div className="hopn-download-app-qr">
                                    <img decoding="async" src={qrCode} alt="QR code"/>
                                </div>
                                <div className="hopn-download-app-links">
                                    <a href="#" className="hopn-app-store-button hopn-app-store-button-apple">
                                        <img decoding="async" src={appStoreButton} alt="Download on App Store"/>
                                    </a>
                                    <a href="#" className="hopn-app-store-button hopn-app-store-button-google">
                                        <img decoding="async" src={playStoreButton} alt="Get it on Google Play"/>
                                    </a>
                                </div>
                            </div>
                            <button type="submit" className="hopn-add-passenger-button no-icon"
                                    onClick={() => setShowAppPopup(false)}>Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// The main Secure Payment step (reached from orderDetails).
export function PaymentCards({setStep, closeModal}) {
    return (
        <>
            <ModalHeader
                title=""
                extraClasses="full-pad-x"
                totalSteps={0}
                cancelButton={false}
                backStep="orderDetails"
                showBackButton={true}
                setStep={setStep}
                closeModal={closeModal}
            />
            <PaymentCardsContent setStep={setStep}/>
        </>
    );
}

// Mobile step for the "Personal Card" tab in selectedCarDetailsLatest (desktop
// renders the same PaymentCardsContent inline as an .upper-modal popup). In
// POPUP_STEPS → opens as a stacked overlay; the ✕ pops back.
export function PersonalCard({setStep, closeModal}) {
    return (
        <>
            <ModalHeader
                title=""
                extraClasses="full-pad-x"
                totalSteps={0}
                cancelButton={true}
                showBackButton={false}
                setStep={setStep}
                closeModal={closeModal}
            />
            <PaymentCardsContent setStep={setStep}/>
        </>
    );
}

export default PaymentCards;
